import {
  type APIChannel,
  formatRelative,
  selectFollowedThreads,
  selectIsUnread,
  selectMentionCount,
  snowflakeDate,
} from '@alternativamais/chat-core';
import { useNavigate } from 'react-router';

import { avisarErro } from '../../../componentes/Avisos';
import { Icone } from '../../../componentes/Icone';
import { useAgora } from '../../../chat/ganchos';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';
import { useNome } from '../../../chat/ganchos';
import { BotaoCaixa } from '../cabecalho/Cabecalho';
import { Vazio } from '../componentes';
import { BotaoGaveta } from '../../../chat/gaveta';

/**
 * Threads que sigo (atalho do início, D1): as threads abertas em que a
 * pessoa entrou, de todos os servidores, a mais movimentada primeiro.
 */
export function ThreadsQueSigo() {
  const threads = useDerivado(selectFollowedThreads, (estado) => [
    estado.joinedThreads,
    estado.channels,
    estado.members,
  ]);
  return (
    <div className="flex min-w-0 grow flex-col bg-chat">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-divisor pr-3 pl-4">
        <BotaoGaveta />
        <Icone nome="thread" tamanho={22} className="text-texto-3" />
        <h1 className="m-0 text-base font-semibold">Threads que sigo</h1>
        <span className="rounded-full bg-ativo px-2 text-xs leading-[22px] font-semibold text-texto-2">
          {threads.length}
        </span>
        <span className="grow" />
        <BotaoCaixa />
      </header>
      <main className="rolagem flex min-h-0 grow flex-col gap-2 overflow-y-auto px-6 py-4">
        {threads.length === 0 ? (
          <Vazio
            icone="thread"
            titulo="Você não segue nenhuma thread"
            texto="Entre numa thread (ou escreva nela) e ela aparece aqui."
          />
        ) : (
          threads.map((thread) => (
            <LinhaThread key={thread.id} thread={thread} />
          ))
        )}
      </main>
    </div>
  );
}

function LinhaThread({ thread }: { thread: APIChannel }) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const agora = useAgora(60_000);
  const pai = useEstado((estado) =>
    thread.parent_id ? estado.channels[thread.parent_id] : undefined,
  );
  const servidor = useEstado((estado) =>
    thread.guild_id ? estado.guilds[thread.guild_id]?.name : undefined,
  );
  const naoLida = useEstado((estado) => selectIsUnread(estado, thread.id));
  const mencoes = useEstado((estado) => selectMentionCount(estado, thread.id));
  const autor = useNome(thread.owner_id ?? '', thread.guild_id);
  const total = thread.message_count ?? 0;
  const abrir = () => {
    if (!pai) return navegar(`/canais/${thread.guild_id}/${thread.id}`);
    navegar(`/canais/${thread.guild_id}/${pai.id}/threads/${thread.id}`);
  };
  return (
    <article className="group flex items-center gap-3 rounded-lg border border-borda bg-elevado px-4 py-3 hover:border-texto-3">
      <Icone nome="thread" tamanho={20} className="shrink-0 text-texto-3" />
      <button
        type="button"
        onClick={abrir}
        className="flex min-w-0 grow flex-col gap-0.5 text-left"
      >
        <span
          className={`truncate text-[15px] ${naoLida ? 'font-bold text-texto' : 'font-semibold text-texto-2'}`}
        >
          {thread.name}
        </span>
        <span className="truncate text-[13px] text-texto-3">
          {pai ? `#${pai.name}` : ''}
          {servidor ? ` · ${servidor}` : ''} · iniciada por {autor} · {total}{' '}
          {total === 1 ? 'mensagem' : 'mensagens'}
          {thread.last_message_id &&
            ` · ativa ${formatRelative(Math.min(snowflakeDate(thread.last_message_id).getTime(), agora), new Date(agora))}`}
        </span>
      </button>
      {mencoes > 0 && (
        <span className="flex h-4 min-w-4 items-center justify-center rounded-lg bg-perigo px-[5px] text-[11px] font-bold text-white">
          {mencoes}
        </span>
      )}
      <button
        type="button"
        onClick={() => cliente.api.leaveThread(thread.id).catch(avisarErro)}
        className="hidden h-8 shrink-0 rounded-lg bg-ativo px-3 text-[13px] font-semibold group-focus-within:block group-hover:block hover:brightness-110"
      >
        Deixar de seguir
      </button>
    </article>
  );
}
